import React, { useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';

interface SafeImageProps {
  src?: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  fallbackSrc?: string;
  sx?: React.CSSProperties | object;
  style?: React.CSSProperties;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
}

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  width,
  height,
  fallbackSrc = 'https://via.placeholder.com/400x200?text=Imagen+no+disponible',
  sx,
  style,
  onError,
  onLoad,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Actualizar imageSrc cuando cambie el prop src
  useEffect(() => {
    setImageSrc(src);
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(false);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
    onError?.(e);
  };

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.(e);
  };

  // Si no hay src, mostrar skeleton
  if (!src) {
    return (
      <Box
        sx={{
          width: width || '100%',
          height: height || 200,
          bgcolor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
        style={style}
      >
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', ...sx }} style={style}>
      {isLoading && (
        <Skeleton 
          variant="rectangular" 
          width={width || '100%'} 
          height={height || 200} 
          sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
        />
      )}
      
      {!hasError && (
        <img
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          onError={handleError}
          onLoad={handleLoad}
          style={{
            width: width || '100%',
            height: height || 'auto',
            display: isLoading ? 'none' : 'block',
            ...style
          }}
          {...props}
        />
      )}
      
      {hasError && (
        <Box
          sx={{
            width: width || '100%',
            height: height || 200,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <Box sx={{ fontSize: '3rem', mb: 1 }}>📷</Box>
            <Box sx={{ fontSize: '0.875rem' }}>Imagen no disponible</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SafeImage; 